import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RealtimeVoiceInterface } from './RealtimeVoiceInterface';
import { UPLOADS_URL } from '@/config/api';
import {
  Video,
  Mic,
  Pause,
  Play,
  Square,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Upload,
  FileVideo,
  Phone
} from 'lucide-react';

// Translations for the UI
const translations = {
  en: {
    pageTitle: 'Record Interview',
    pageSubtitle: 'Choose to record live or upload a pre-recorded file',
    aiVoiceInterview: 'AI Voice Interview',
    liveRecording: 'Live Recording',
    uploadFile: 'Upload File',
    question: 'Question',
    of: 'of',
    duration: 'Duration',
    currentQuestion: 'Current Question',
    noQuestions: 'No questions available',
    previous: 'Previous',
    next: 'Next',
    allQuestions: 'All Questions',
    interviewProgress: 'Interview Progress',
    startRecording: 'Start Recording',
    pause: 'Pause',
    resume: 'Resume',
    stopUpload: 'Stop & Upload',
    uploadingRecording: 'Uploading recording...',
    complete: 'complete',
    dropFileHere: 'Drop your video or audio file here',
    fileSupport: 'Supports MP4, MOV, WebM video or MP3, WAV audio files (max 500MB)',
    chooseFile: 'Choose File',
    remove: 'Remove',
    uploadStartTranscription: 'Upload & Start Transcription',
    uploadingTranscribing: 'Uploading and transcribing...',
    autoTranscription: 'Your file is being uploaded and will be automatically transcribed with speaker identification',
    aboutFileUpload: 'About File Upload',
    autoTranscriptionFeature: 'Automatic Transcription:',
    autoTranscriptionDesc: 'Your file will be automatically transcribed with speaker identification.',
    speakerDiarization: 'Speaker Diarization:',
    speakerDiarizationDesc: 'The system will identify the journalist and expert automatically.',
    supportedFormats: 'Supported Formats:',
    supportedFormatsDesc: 'MP4, MOV, WebM video or MP3, WAV audio files.',
    fileSize: 'File Size:',
    fileSizeDesc: 'Maximum 500MB per file.',
    invalidFileType: 'Invalid file type. Please upload MP4, MOV, WebM video or MP3, WAV audio files.',
    fileTooLarge: 'File too large. Maximum size is 500MB.',
    uploadFailed: 'Upload failed. Please try again.',
    connectionError: 'Upload failed. Please check your connection and try again.',
    initMediaError: 'Failed to initialize camera and microphone. Please check your permissions.',
    startRecordingError: 'Failed to start recording. Please try again.'
  },
  es: {
    pageTitle: 'Grabar Entrevista',
    pageSubtitle: 'Elija grabar en vivo o subir un archivo pregrabado',
    aiVoiceInterview: 'Entrevista por Voz IA',
    liveRecording: 'Grabación en Vivo',
    uploadFile: 'Subir Archivo',
    question: 'Pregunta',
    of: 'de',
    duration: 'Duración',
    currentQuestion: 'Pregunta Actual',
    noQuestions: 'No hay preguntas disponibles',
    previous: 'Anterior',
    next: 'Siguiente',
    allQuestions: 'Todas las Preguntas',
    interviewProgress: 'Progreso de la Entrevista',
    startRecording: 'Comenzar Grabación',
    pause: 'Pausar',
    resume: 'Reanudar',
    stopUpload: 'Detener y Subir',
    uploadingRecording: 'Subiendo grabación...',
    complete: 'completo',
    dropFileHere: 'Suelte su archivo de video o audio aquí',
    fileSupport: 'Soporta archivos de video MP4, MOV, WebM o audio MP3, WAV (máx 500MB)',
    chooseFile: 'Elegir Archivo',
    remove: 'Eliminar',
    uploadStartTranscription: 'Subir e Iniciar Transcripción',
    uploadingTranscribing: 'Subiendo y transcribiendo...',
    autoTranscription: 'Su archivo se está subiendo y será transcrito automáticamente con identificación de hablantes',
    aboutFileUpload: 'Sobre la Subida de Archivos',
    autoTranscriptionFeature: 'Transcripción Automática:',
    autoTranscriptionDesc: 'Su archivo será transcrito automáticamente con identificación de hablantes.',
    speakerDiarization: 'Identificación de Hablantes:',
    speakerDiarizationDesc: 'El sistema identificará al periodista y al experto automáticamente.',
    supportedFormats: 'Formatos Soportados:',
    supportedFormatsDesc: 'Archivos de video MP4, MOV, WebM o audio MP3, WAV.',
    fileSize: 'Tamaño de Archivo:',
    fileSizeDesc: 'Máximo 500MB por archivo.',
    invalidFileType: 'Tipo de archivo inválido. Por favor suba archivos de video MP4, MOV, WebM o audio MP3, WAV.',
    fileTooLarge: 'Archivo muy grande. El tamaño máximo es 500MB.',
    uploadFailed: 'Error al subir. Por favor inténtelo de nuevo.',
    connectionError: 'Error al subir. Por favor verifique su conexión e inténtelo de nuevo.',
    initMediaError: 'Error al inicializar cámara y micrófono. Por favor verifique sus permisos.',
    startRecordingError: 'Error al comenzar la grabación. Por favor inténtelo de nuevo.'
  },
  ca: {
    pageTitle: 'Gravar Entrevista',
    pageSubtitle: 'Trieu gravar en directe o pujar un arxiu pregravat',
    aiVoiceInterview: 'Entrevista per Veu IA',
    liveRecording: 'Gravació en Directe',
    uploadFile: 'Pujar Arxiu',
    question: 'Pregunta',
    of: 'de',
    duration: 'Durada',
    currentQuestion: 'Pregunta Actual',
    noQuestions: 'No hi ha preguntes disponibles',
    previous: 'Anterior',
    next: 'Següent',
    allQuestions: 'Totes les Preguntes',
    interviewProgress: "Progrés de l'Entrevista",
    startRecording: 'Començar Gravació',
    pause: 'Pausar',
    resume: 'Reprendre',
    stopUpload: 'Aturar i Pujar',
    uploadingRecording: 'Pujant gravació...',
    complete: 'complet',
    dropFileHere: 'Deixeu el vostre arxiu de vídeo o àudio aquí',
    fileSupport: "Suporta arxius de vídeo MP4, MOV, WebM o àudio MP3, WAV (màx 500MB)",
    chooseFile: 'Triar Arxiu',
    remove: 'Eliminar',
    uploadStartTranscription: 'Pujar i Iniciar Transcripció',
    uploadingTranscribing: 'Pujant i transcrivint...',
    autoTranscription: "El vostre arxiu s'està pujant i serà transcrit automàticament amb identificació de parlants",
    aboutFileUpload: "Sobre la Pujada d'Arxius",
    autoTranscriptionFeature: 'Transcripció Automàtica:',
    autoTranscriptionDesc: 'El vostre arxiu serà transcrit automàticament amb identificació de parlants.',
    speakerDiarization: 'Identificació de Parlants:',
    speakerDiarizationDesc: "El sistema identificarà el periodista i l'expert automàticament.",
    supportedFormats: 'Formats Suportats:',
    supportedFormatsDesc: 'Arxius de vídeo MP4, MOV, WebM o àudio MP3, WAV.',
    fileSize: "Mida d'Arxiu:",
    fileSizeDesc: 'Màxim 500MB per arxiu.',
    invalidFileType: "Tipus d'arxiu invàlid. Si us plau pugeu arxius de vídeo MP4, MOV, WebM o àudio MP3, WAV.",
    fileTooLarge: 'Arxiu massa gran. La mida màxima és 500MB.',
    uploadFailed: 'Error en pujar. Si us plau torneu-ho a provar.',
    connectionError: 'Error en pujar. Si us plau verifiqueu la vostra connexió i torneu-ho a provar.',
    initMediaError: 'Error en inicialitzar càmera i micròfon. Si us plau verifiqueu els vostres permisos.',
    startRecordingError: 'Error en començar la gravació. Si us plau torneu-ho a provar.'
  }
};

type Language = keyof typeof translations;

interface ExpertRecordingScreenProps {
  interview: {
    id: string;
    expertName: string;
    language?: string;
    journalist: {
      name: string;
      photoUrl?: string;
    };
    questions: Array<{
      id: string;
      text: string;
      type: string;
    }>;
  };
  token: string;
  onComplete: () => void;
  selectedAudioDevice?: string;
}

type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped' | 'uploading';

export function ExpertRecordingScreen({ interview, token, onComplete, selectedAudioDevice }: ExpertRecordingScreenProps) {
  // Debug: Log the selectedAudioDevice prop value
  console.log('[ExpertRecordingScreen] 📹 Received selectedAudioDevice prop:', selectedAudioDevice);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('realtime');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get translations for the interview language
  const lang = (interview.language || 'en') as Language;
  const t = translations[lang] || translations.en;

  const totalQuestions = interview.questions?.length || 0;
  const currentQuestion = totalQuestions > 0 ? interview.questions[currentQuestionIndex] : null;
  const journalistInitials = interview.journalist.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  // Initialize media when "live" or "realtime" tab becomes active
  // Note: The realtime tab (RealtimeVoiceInterface) handles its own media initialization,
  // so we only need to initialize for "live" tab
  useEffect(() => {
    if (activeTab === 'live') {
      console.log('[Recording] Live tab activated, initializing media...');
      initializeMedia();
    }

    return () => {
      if (streamRef.current) {
        console.log('[Recording] Cleaning up media stream...');
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current = null;
      }
    };
  }, [activeTab]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (recordingState === 'recording') {
      interval = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recordingState]);

  const initializeMedia = async () => {
    try {
      console.log('[Recording] Initializing media, selectedAudioDevice:', selectedAudioDevice);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: selectedAudioDevice
          ? {
              deviceId: { ideal: selectedAudioDevice }, // Use 'ideal' instead of 'exact' for better fallback
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 24000,
              channelCount: 1
            }
          : {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 24000,
              channelCount: 1
            }
      });

      console.log('[Recording] Media stream obtained:', stream.getTracks().map(t => ({ kind: t.kind, label: t.label })));
      streamRef.current = stream;

      // Set video preview - retry if ref not ready
      const setVideoStream = () => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          console.log('[Recording] Video stream set successfully');

          // Force video to play - critical for Safari and some browsers
          videoRef.current.onloadedmetadata = () => {
            console.log('[Recording] Video metadata loaded, attempting to play');
            videoRef.current?.play()
              .then(() => console.log('[Recording] Video element playing'))
              .catch(playError => console.error('[Recording] Error playing video:', playError));
          };
          return true;
        } else {
          console.warn('[Recording] Video ref not ready, will retry...');
          return false;
        }
      };

      // Try to set video stream immediately, with retry mechanism
      const videoRefReady = setVideoStream();
      if (!videoRefReady) {
        // Video ref not ready, wait a bit and retry
        setTimeout(() => {
          console.log('[Recording] Retrying video stream setup...');
          const retrySuccess = setVideoStream();
          if (!retrySuccess) {
            console.error('[Recording] Video ref is still null after retry - video element may not be rendered');
          }
        }, 100);
      }

      // Initialize MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        handleRecordingStopped();
      };

      mediaRecorderRef.current = mediaRecorder;
    } catch (err) {
      console.error('Error initializing media:', err);
      setError(t.initMediaError);
    }
  };

  const startRecording = () => {
    if (!mediaRecorderRef.current) return;

    try {
      chunksRef.current = [];
      mediaRecorderRef.current.start(1000); // Capture in 1s chunks
      setRecordingState('recording');
      setError(null);
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(t.startRecordingError);
    }
  };

  const pauseRecording = () => {
    if (!mediaRecorderRef.current || recordingState !== 'recording') return;

    try {
      mediaRecorderRef.current.pause();
      setRecordingState('paused');
    } catch (err) {
      console.error('Error pausing recording:', err);
    }
  };

  const resumeRecording = () => {
    if (!mediaRecorderRef.current || recordingState !== 'paused') return;

    try {
      mediaRecorderRef.current.resume();
      setRecordingState('recording');
    } catch (err) {
      console.error('Error resuming recording:', err);
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;

    try {
      mediaRecorderRef.current.stop();
      setRecordingState('stopped');

      // Stop the stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    } catch (err) {
      console.error('Error stopping recording:', err);
    }
  };

  const handleRecordingStopped = async () => {
    const blob = new Blob(chunksRef.current, { type: 'video/webm' });
    await uploadRecording(blob);
  };

  const uploadRecording = async (blob: Blob) => {
    setRecordingState('uploading');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('recording', blob, 'interview-recording.webm');
      formData.append('duration', duration.toString());

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onComplete();
        } else {
          setError(t.uploadFailed);
          setRecordingState('stopped');
        }
      });

      xhr.addEventListener('error', () => {
        setError(t.connectionError);
        setRecordingState('stopped');
      });

      // Use UPLOADS_URL to bypass Cloudflare for large audio/video files
      xhr.open('POST', `${UPLOADS_URL}/expert/interview/recording?token=${encodeURIComponent(token)}`);
      xhr.send(formData);

    } catch (err) {
      console.error('Error uploading recording:', err);
      setError(t.uploadFailed);
      setRecordingState('stopped');
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(i => i + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(i => i - 1);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    const maxSize = 500 * 1024 * 1024; // 500MB
    const allowedTypes = [
      'video/mp4',
      'video/quicktime',
      'video/webm',
      'audio/mp3',
      'audio/mpeg',
      'audio/wav',
      'audio/webm'
    ];

    if (!allowedTypes.includes(file.type)) {
      return t.invalidFileType;
    }

    if (file.size > maxSize) {
      return t.fileTooLarge;
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile) return;

    setRecordingState('uploading');
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('media', selectedFile);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          console.log('Upload successful:', response);

          // Show success message briefly before completing
          setTimeout(() => {
            onComplete();
          }, 1500);
        } else {
          const response = JSON.parse(xhr.responseText);
          setError(response.error || t.uploadFailed);
          setRecordingState('idle');
          setSelectedFile(null);
        }
      });

      xhr.addEventListener('error', () => {
        setError(t.connectionError);
        setRecordingState('idle');
        setSelectedFile(null);
      });

      // Use UPLOADS_URL to bypass Cloudflare for large audio/video files
      xhr.open('POST', `${UPLOADS_URL}/expert/interview/upload-media?token=${encodeURIComponent(token)}`);
      xhr.send(formData);

    } catch (err) {
      console.error('Error uploading file:', err);
      setError(t.uploadFailed);
      setRecordingState('idle');
      setSelectedFile(null);
    }
  };

  return (
    <div className="min-h-screen p-4 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">{t.pageTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {t.pageSubtitle}
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs
          defaultValue="realtime"
          className="w-full"
          onValueChange={(value) => {
            console.log('[Recording] Tab changed to:', value);
            setActiveTab(value);
          }}
        >
          <TabsList className="grid w-full max-w-3xl grid-cols-3">
            <TabsTrigger value="realtime" disabled={recordingState === 'uploading'}>
              <Phone className="w-4 h-4 mr-2" />
              {t.aiVoiceInterview}
            </TabsTrigger>
            <TabsTrigger value="live" disabled={recordingState === 'uploading'}>
              <Video className="w-4 h-4 mr-2" />
              {t.liveRecording}
            </TabsTrigger>
            <TabsTrigger value="upload" disabled={recordingState === 'uploading'}>
              <Upload className="w-4 h-4 mr-2" />
              {t.uploadFile}
            </TabsTrigger>
          </TabsList>

          {/* Real-time Voice Interview Tab */}
          <TabsContent value="realtime" className="space-y-6">
            <RealtimeVoiceInterface
              interview={{
                id: interview.id,
                expertName: interview.expertName,
                journalist: interview.journalist,
                questions: interview.questions,
                language: interview.language
              }}
              token={token}
              onComplete={onComplete}
              selectedAudioDevice={selectedAudioDevice}
            />
          </TabsContent>

          {/* Live Recording Tab */}
          <TabsContent value="live" className="space-y-6">
            {/* Duration Display */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t.question} {currentQuestionIndex + 1} {t.of} {totalQuestions}
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{t.duration}</p>
                  <p className="text-2xl font-mono font-bold">{formatDuration(duration)}</p>
                </div>

                {recordingState === 'recording' && (
                  <div className="w-4 h-4 rounded-full bg-red-600 animate-pulse" />
                )}
              </div>
            </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video Preview (2/3 width on large screens) */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardContent className="p-0">
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />

                  {/* Journalist Avatar Overlay */}
                  <div className="absolute top-4 left-4 flex items-center gap-3 bg-black/70 px-4 py-2 rounded-full">
                    <Avatar className="h-10 w-10 border-2 border-white">
                      {interview.journalist.photoUrl && (
                        <AvatarImage src={interview.journalist.photoUrl} />
                      )}
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {journalistInitials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-white font-medium">{interview.journalist.name}</span>
                  </div>

                  {/* Recording Indicator */}
                  {recordingState === 'recording' && (
                    <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-600 px-4 py-2 rounded-full">
                      <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
                      <span className="text-white font-medium">REC</span>
                    </div>
                  )}

                  {recordingState === 'paused' && (
                    <div className="absolute top-4 right-4 bg-yellow-600 px-4 py-2 rounded-full">
                      <span className="text-white font-medium">PAUSED</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recording Controls */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-4">
                  {recordingState === 'idle' && (
                    <Button
                      onClick={startRecording}
                      size="lg"
                      className="px-8 bg-red-600 hover:bg-red-700"
                    >
                      <Video className="w-5 h-5 mr-2" />
                      {t.startRecording}
                    </Button>
                  )}

                  {(recordingState === 'recording' || recordingState === 'paused') && (
                    <>
                      {recordingState === 'recording' ? (
                        <Button
                          onClick={pauseRecording}
                          size="lg"
                          variant="outline"
                        >
                          <Pause className="w-5 h-5 mr-2" />
                          {t.pause}
                        </Button>
                      ) : (
                        <Button
                          onClick={resumeRecording}
                          size="lg"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Play className="w-5 h-5 mr-2" />
                          {t.resume}
                        </Button>
                      )}

                      <Button
                        onClick={stopRecording}
                        size="lg"
                        variant="destructive"
                      >
                        <Square className="w-5 h-5 mr-2" />
                        {t.stopUpload}
                      </Button>
                    </>
                  )}

                  {recordingState === 'uploading' && (
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        <span className="font-medium">{t.uploadingRecording}</span>
                      </div>
                      <Progress value={uploadProgress} className="h-2" />
                      <p className="text-center text-sm text-muted-foreground">
                        {Math.round(uploadProgress)}% {t.complete}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Questions Panel (1/3 width on large screens) */}
          <div className="space-y-4">
            {/* Current Question */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">{t.currentQuestion}</span>
                    <span className="text-sm font-medium">
                      {currentQuestionIndex + 1}/{totalQuestions}
                    </span>
                  </div>

                  <div className="p-4 bg-primary/5 rounded-lg border-2 border-primary">
                    <p className="text-lg font-medium leading-relaxed">{currentQuestion?.text || t.noQuestions}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={previousQuestion}
                      disabled={currentQuestionIndex === 0 || recordingState === 'uploading'}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      {t.previous}
                    </Button>
                    <Button
                      onClick={nextQuestion}
                      disabled={currentQuestionIndex === totalQuestions - 1 || recordingState === 'uploading'}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      {t.next}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* All Questions */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">{t.allQuestions}</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {interview.questions.map((question, index) => (
                    <button
                      key={question.id}
                      onClick={() => setCurrentQuestionIndex(index)}
                      disabled={recordingState === 'uploading'}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        index === currentQuestionIndex
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      <div className="flex gap-2">
                        <span className="font-semibold flex-shrink-0">{index + 1}.</span>
                        <span className="text-sm">{question.text}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Progress */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t.interviewProgress}</span>
                    <span>{totalQuestions > 0 ? Math.round((currentQuestionIndex + 1) / totalQuestions * 100) : 0}%</span>
                  </div>
                  <Progress
                    value={totalQuestions > 0 ? (currentQuestionIndex + 1) / totalQuestions * 100 : 0}
                    className="h-2"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>

          {/* Upload File Tab */}
          <TabsContent value="upload" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload Area (2/3 width) */}
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardContent className="p-6">
                    {!selectedFile ? (
                      <div
                        onDrop={handleFileDrop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                          isDragging
                            ? 'border-primary bg-primary/5'
                            : 'border-muted-foreground/25 hover:border-primary/50'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                            <FileVideo className="w-8 h-8 text-primary" />
                          </div>

                          <div>
                            <h3 className="text-lg font-semibold mb-2">
                              {t.dropFileHere}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4">
                              {t.fileSupport}
                            </p>
                          </div>

                          <Button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            variant="outline"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {t.chooseFile}
                          </Button>

                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/mp4,video/quicktime,video/webm,audio/mp3,audio/mpeg,audio/wav,audio/webm"
                            onChange={handleFileInput}
                            className="hidden"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center">
                              <FileVideo className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{selectedFile.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatFileSize(selectedFile.size)}
                              </p>
                            </div>
                          </div>

                          <Button
                            type="button"
                            onClick={() => setSelectedFile(null)}
                            variant="ghost"
                            size="sm"
                            disabled={recordingState === 'uploading'}
                          >
                            {t.remove}
                          </Button>
                        </div>

                        {recordingState !== 'uploading' ? (
                          <Button
                            onClick={uploadSelectedFile}
                            size="lg"
                            className="w-full"
                          >
                            <Upload className="w-5 h-5 mr-2" />
                            {t.uploadStartTranscription}
                          </Button>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center gap-3 py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-primary" />
                              <span className="font-medium">
                                {t.uploadingTranscribing}
                              </span>
                            </div>
                            <Progress value={uploadProgress} className="h-2" />
                            <p className="text-center text-sm text-muted-foreground">
                              {Math.round(uploadProgress)}% {t.complete}
                            </p>
                            <p className="text-center text-sm text-muted-foreground">
                              {t.autoTranscription}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Info Card */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-3">{t.aboutFileUpload}</h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>
                        <strong>✓ {t.autoTranscriptionFeature}</strong> {t.autoTranscriptionDesc}
                      </p>
                      <p>
                        <strong>✓ {t.speakerDiarization}</strong> {t.speakerDiarizationDesc}
                      </p>
                      <p>
                        <strong>✓ {t.supportedFormats}</strong> {t.supportedFormatsDesc}
                      </p>
                      <p>
                        <strong>✓ {t.fileSize}</strong> {t.fileSizeDesc}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Questions Panel (1/3 width) - Reuse from live recording */}
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          {t.currentQuestion}
                        </span>
                        <span className="text-sm font-medium">
                          {currentQuestionIndex + 1}/{totalQuestions}
                        </span>
                      </div>

                      <div className="p-4 bg-primary/5 rounded-lg border-2 border-primary">
                        <p className="text-lg font-medium leading-relaxed">
                          {currentQuestion?.text || t.noQuestions}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={previousQuestion}
                          disabled={currentQuestionIndex === 0}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          {t.previous}
                        </Button>
                        <Button
                          onClick={nextQuestion}
                          disabled={currentQuestionIndex === totalQuestions - 1}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          {t.next}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3">{t.allQuestions}</h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {interview.questions.map((question, index) => (
                        <button
                          key={question.id}
                          onClick={() => setCurrentQuestionIndex(index)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            index === currentQuestionIndex
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                        >
                          <div className="flex gap-2">
                            <span className="font-semibold flex-shrink-0">{index + 1}.</span>
                            <span className="text-sm">{question.text}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{t.interviewProgress}</span>
                        <span>
                          {totalQuestions > 0 ? Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100) : 0}%
                        </span>
                      </div>
                      <Progress
                        value={totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0}
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
